/**
 * Featured-image hover for the homepage and collection galleries.
 *
 * The grid card and the large image beside it often use different files, so
 * the preview is not already decoded when the pointer arrives. Writing the
 * next src and aspect ratio onto the visible <img> before that decode finishes
 * lays the outgoing photo out in the incoming frame: it shrinks for a moment,
 * then the new file pops in. Commit only after the next file is ready so the
 * current photo keeps its size until the swap.
 */

/**
 * @param {HTMLElement} el
 * @returns {{ url: string, image: string, imageWidth: number, imageHeight: number, price: string, title: string }}
 */
function readGalleryCard(el) {
  return {
    url: el.dataset.url || "",
    image: el.dataset.image || "",
    imageWidth: Number(el.dataset.imageWidth) || 1,
    imageHeight: Number(el.dataset.imageHeight) || 1,
    price: el.dataset.price || "",
    title: el.dataset.title || "",
  };
}

/** @type {Map<string, Promise<void>>} */
const decodedGalleryImages = new Map();

/**
 * Decode a gallery preview into the HTTP cache before it is shown.
 * A failed file is not cached, so a later hover can try again.
 *
 * @param {string} src
 * @returns {Promise<void>}
 */
function preloadGalleryImage(src) {
  const cached = decodedGalleryImages.get(src);
  if (cached) return cached;

  const pending = new Promise((resolve, reject) => {
    const image = new Image();
    const finish = () => resolve();
    const fail = () => reject(new Error(`Failed to preload gallery image: ${src}`));
    image.onload = () => {
      if (typeof image.decode === "function") image.decode().then(finish, finish);
      else finish();
    };
    image.onerror = fail;
    image.src = src;
    if (image.complete) {
      if (image.naturalWidth > 0) finish();
      else fail();
    }
  }).catch((error) => {
    decodedGalleryImages.delete(src);
    throw error;
  });

  decodedGalleryImages.set(src, pending);
  return pending;
}

/**
 * @param {{ preload: (src: string) => Promise<void>, apply: (next: ReturnType<typeof readGalleryCard>) => void }} options
 */
function createHomeGalleryController({ preload, apply }) {
  let requestId = 0;
  /** @type {HTMLElement | null} */
  let pendingEl = null;
  /** @type {Promise<boolean> | null} */
  let pendingPromise = null;
  let currentImage = "";

  return {
    /**
     * @param {string} src
     */
    setCurrentImage(src) {
      currentImage = src || "";
    },

    /**
     * Show this card once its preview is decoded.
     * A newer card cancels an in-flight swap. Repeating the card that is
     * already loading returns that same promise instead of starting another.
     *
     * @param {HTMLElement} el
     * @returns {Promise<boolean>} true when this card was applied
     */
    commit(el) {
      if (pendingEl === el && pendingPromise) return pendingPromise;

      const next = readGalleryCard(el);
      const token = ++requestId;
      pendingEl = el;

      if (!next.image || next.image === currentImage) {
        currentImage = next.image;
        apply(next);
        pendingPromise = Promise.resolve(true);
        return pendingPromise;
      }

      const promise = preload(next.image).then(
        () => {
          if (token !== requestId) return false;
          currentImage = next.image;
          apply(next);
          return true;
        },
        () => {
          if (token !== requestId) return false;
          currentImage = next.image;
          apply(next);
          return true;
        },
      );
      pendingPromise = promise;
      return promise;
    },
  };
}

/**
 * Alpine component for snippets/product-gallery.liquid.
 * @returns {object}
 */
function VagueHomeGallery() {
  return {
    url: "",
    image: "",
    imageWidth: 1,
    imageHeight: 1,
    price: "",
    /** @type {ReturnType<typeof createHomeGalleryController> | null} */
    controller: null,
    init() {
      this.controller = createHomeGalleryController({
        preload: preloadGalleryImage,
        apply: (next) => {
          this.url = next.url;
          this.image = next.image;
          this.imageWidth = next.imageWidth;
          this.imageHeight = next.imageHeight;
          this.price = next.price;
          this.$store.homeGallery.title = next.title;
        },
      });

      const image = this.$el.dataset.initialImage || "";
      this.controller.setCurrentImage(image);
      this.url = this.$el.dataset.initialUrl || "";
      this.image = image;
      this.imageWidth = Number(this.$el.dataset.initialWidth) || 1;
      this.imageHeight = Number(this.$el.dataset.initialHeight) || 1;
      this.price = this.$el.dataset.initialPrice || "";
      this.$store.homeGallery.title = this.$el.dataset.initialTitle || "";
    },
    /**
     * @param {Event} event
     * @returns {HTMLElement | null}
     */
    cardLink(event) {
      const target = event.target;
      if (!(target instanceof Element) || typeof target.closest !== "function") return null;
      const el = target.closest("[data-url]");
      if (!(el instanceof HTMLElement) || !this.$el.contains(el)) return null;
      return el;
    },
    /**
     * @param {Event} event
     */
    handleCardPointer(event) {
      const el = this.cardLink(event);
      if (!el || !this.controller) return;
      this.controller.commit(el);
    },
    /**
     * @param {Event} event
     */
    handleCardClick(event) {
      const el = this.cardLink(event);
      if (!el || !this.controller) return;
      if (!window.matchMedia("(max-width: 767px)").matches) return;
      event.preventDefault();
      this.controller.commit(el).then((applied) => {
        if (applied) this.$refs.modal.showModal();
      });
    },
    closeModal() {
      this.$refs.modal.close();
    },
  };
}

if (typeof window !== "undefined") {
  window.VagueHomeGallery = VagueHomeGallery;
}

if (typeof module !== "undefined" && module.exports) {
  module.exports = {
    readGalleryCard,
    createHomeGalleryController,
    preloadGalleryImage,
    VagueHomeGallery,
  };
}
